import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import styled from 'styled-components';
import { IndicatorDetailsHandler } from '../../../../NPOFeedback/FeedbackDetails/ImpactDetailsEdit/utils';
import { IndicatorDetailSectionProps, IndicatorDetails } from '../utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { getMadLibForIndicator } from '../../ImpactIndicator/IndicatorMadLibs';
import IndicatorBottomButtons from '../IndicatorBottomButtons';
import { SelectorOptions } from 'shared/design-system/components/Selector';

const dosageOptions = [
  'One day',
  'Multiple days',
  'Multiple weeks',
  'Multiple months',
];

const StyledDiv = styled.div`
  .dosage-heading {
    max-width: 730px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 40px;
  }
`;

const validateDosageNext = (
  dosage: IndicatorDetails['interventionDosageType'],
) => Boolean(dosage);

const IndicatorDosage = React.forwardRef<
  IndicatorDetailsHandler,
  IndicatorDetailSectionProps & {
    indicatorDetails: IndicatorDetails;
  }
>(
  (
    {
      indicator,
      hideBottomButtons,
      requestState,
      indicatorDetails,
      selectedIndex,
      isReportPassed,
      beneficiaryTerm,
      selectedSectionKey,
      className,
      setIndicators,
      onNext,
      onPrevious,
      handleIndicatorSave,
    },
    forwardRef,
  ) => {
    const [dosage, setDosage] = useState<string | undefined>(
      indicatorDetails.interventionDosageType,
    );

    useEffect(() => {
      setDosage(indicatorDetails.interventionDosageType);
    }, [indicatorDetails]);

    useImperativeHandle(forwardRef, () => ({
      answer: () => ({
        answer: dosage,
        errorText: validateDosageNext(dosage)
          ? undefined
          : 'Please select a valid dosage option.',
      }),
    }));

    const handleNext = useCallback(() => {
      onNext({ interventionDosageType: dosage });
      setIndicators(s => {
        const newState = { ...s };
        newState[selectedSectionKey][
          selectedIndex
        ].interventionDosageType = dosage;
        return newState;
      });
      const newIndicatorDetails = { ...indicatorDetails };
      newIndicatorDetails.interventionDosageType = dosage;
      handleIndicatorSave(newIndicatorDetails);
    }, [
      dosage,
      selectedIndex,
      indicatorDetails,
      selectedSectionKey,
      onNext,
      setIndicators,
      handleIndicatorSave,
    ]);

    const handleClick = (
      newDosage: IndicatorDetails['interventionDosageType'],
    ) => {
      setDosage(newDosage);
      /*if (dosage === undefined) {
        handleNext(newDosage);
      }*/
    };

    const initialUnit = getMadLibForIndicator(indicator)?.unit;
    const lowerCaseBeneficiaryTerm = beneficiaryTerm?.toLowerCase();
    const unit =
      (initialUnit === 'people'
        ? lowerCaseBeneficiaryTerm === 'animals'
          ? 'people'
          : lowerCaseBeneficiaryTerm
        : initialUnit) || 'people';

    return (
      <StyledDiv className={className}>
        <TrueImpactText variant="heading-page" className="dosage-heading">
          What is the average duration {unit} are served by, interact with, or
          participate in your program?
        </TrueImpactText>
        <SelectorOptions
          variant="radio"
          options={dosageOptions}
          getLabel={val => val}
          getSelected={val => val === dosage}
          onClick={handleClick}
        />
        {!hideBottomButtons && (
          <IndicatorBottomButtons
            requestState={requestState}
            disabledNext={!validateDosageNext(dosage)}
            onNext={() => handleNext()}
            onPrevious={onPrevious}
          />
        )}
      </StyledDiv>
    );
  },
);

export default IndicatorDosage;
